import { ICheckedEntity } from '@se/se.platform.base';
import { IDataSourceItem } from '@se/cp.common';
import { ICpEntity, ICpEntityList } from '@se/cp.engine';
import { DataBinding } from '@se/se.platform.base';
import { CLINICAL_EVAL_COMBO_SOURCE_FOREIGN_KEY, SITE_SOURCE_FOREIGN_KEY } from './config';
import { IClinicalEvalAnswer, IKineticPrint } from './interfaces';

export const isClinicalEvaluationItem = (item: ICheckedEntity): boolean =>
    !!item.getPropertyPrimitiveValue(CLINICAL_EVAL_COMBO_SOURCE_FOREIGN_KEY);

export const isClinicalEvaluationItemList = (itemList: ICpEntityList): boolean => {
    const items = itemList.getAllEntities(new DataBinding());
    return items?.reduce((prev, current) => prev && isClinicalEvaluationItem(current), true) ?? false;
};

export const isClinicalEvaluationCombo = (item: ICheckedEntity) =>
    !!item.getPropertyPrimitiveValue(SITE_SOURCE_FOREIGN_KEY);

export const getDescriptionForPrint = (item: IDataSourceItem, fallback: boolean = false): string =>
    fallback
        ? (item?.getPropertyPrimitiveValue('DescriptionForPrint') as string) ||
          (item?.getPropertyPrimitiveValue('Description') as string) ||
          ''
        : (item?.getPropertyPrimitiveValue('DescriptionForPrint') as string) || '';

export const getReport = (item: ICpEntity, fallback: boolean = false): string =>
    fallback
        ? (item?.getPropertyPrimitiveValue('Report') as string) ||
          (item?.getPropertyPrimitiveValue('Description') as string) ||
          ''
        : (item?.getPropertyPrimitiveValue('Report') as string) || '';

export const getDescription = (item: IDataSourceItem): string =>
    (item?.getPropertyPrimitiveValue('Description') as string) || '';

export const getId = (item: IDataSourceItem): string => (item?.getPropertyPrimitiveValue('Id') as string) || '';

const _sort = (list: (IKineticPrint | IClinicalEvalAnswer)[][]): void => {
    list.sort((a, b) => {
        return (a[0]?.idx ?? 0) - (b[0]?.idx ?? 0);
    });
};

export const sortClinicalEvalAnswer = _sort;

export const sortKineticPrint = _sort;

export const isEmpty = (val: any): val is undefined | null => val === undefined || val === null;
export const isNotEmpty = <T>(val: T | undefined | null): val is T => !isEmpty(val);

export const clearHTMLString = (strToSanitize: string): string => {
    return strToSanitize
        .replace(/(<([^>]+)>)/gi, '')
        .replace(/\n/g, '')
        .trim();
};

export const getKineticListHtmlRow = (
    kineticList: IKineticPrint[][],
    labelNewLine: boolean,
    valueNewLine: boolean,
    segmentLabelSeparator: string,
    segmentValueSeparator: string,
): string[] => {
    return kineticList.map((list) =>
        list
            .map(({ label, value }, index) => {
                if (index === 0) {
                    if (labelNewLine && valueNewLine) return `<p>${label}</p><p>${value}</p>`;
                    else if (labelNewLine) return `<p>${label}</p>${value}`;
                    else if (valueNewLine) return `${label}${segmentLabelSeparator}<p>${value}</p>`;
                    else return `${label}${segmentLabelSeparator}${value}`;
                }
                return valueNewLine ? `<p>${value}</p>` : `${value}`;
            })
            .join(valueNewLine ? '' : segmentValueSeparator),
    );
};

export const getClinicalEvalTextToPrint = (
    listToPrint: IClinicalEvalAnswer[][],
    siteNewLine: boolean,
    valueNewLine: boolean,
    siteInBold: boolean,
    siteSeparator: string,
    valueSeparator: string,
    hideSite: boolean,
) => {
    return listToPrint.map((list) =>
        list
            .map((obj, i) => {
                const site = siteInBold ? `<strong>${obj.site}</strong>` : obj.site;
                if (i === 0) {
                    if ((!obj.site || hideSite) && valueNewLine) return `<p>${obj.value}</p>`;
                    if (!obj.site || hideSite) return `${obj.value}`;
                    if (siteNewLine && valueNewLine) return `<p>${site}</p><p>${obj.value}</p>`;
                    if (siteNewLine) return `<p>${site}</p>${obj.value}`;
                    if (valueNewLine) return `${site}${siteSeparator}<p>${obj.value}</p>`;
                    return `${site}${siteSeparator} ${obj.value}`;
                }
                return valueNewLine ? `<p>${obj.value}</p>` : `${obj.value}`;
            })
            .join(valueNewLine ? '' : valueSeparator),
    );
};
